<ng-container *ngIf="isLoading; else testContent">
  <app-loader />
</ng-container>

<ng-template #testContent>

  <!-- ── Error State ─────────────────────────────────────── -->
  <div *ngIf="hasError" class="container py-5">
    <div class="alert alert-danger text-center" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      <p>{{ errorMessage }}</p>
      <button class="btn btn-primary mt-3" routerLink="/student/tests">
        <i class="fas fa-arrow-left me-2"></i>
        {{ 'BUTTONS.BACK_TO_TESTS' | translate }}
      </button>
    </div>
  </div>

  <!-- ── Fullscreen Test Overlay ─────────────────────────── -->
  <div *ngIf="!hasError && session" class="test-overlay" role="main">

    <!-- ── Header ──────────────────────────────────────────── -->
    <div class="test-header">
      <app-quiz-header
        [iconStart]="'fa fa-clock'"
        [progressBar]="true"
        [progressValue]="progressValue"
        [customClasses]="'p-3'"
        [totalSeconds]="session.timeRemaining"
        [showTestTimer]="session.hasTimeLimit"
        [questionTimeSeconds]="session.questionTimeRemaining"
        [showQuestionTimer]="currentQuestion?.question?.timeLimitSeconds! > 0">
        <div class="d-flex justify-content-between align-items-center w-100">
          <div class="d-flex flex-column">
            <span class="fs-6 fw-semibold text-dark">{{ session.test.title }}</span>
            <small class="text-muted">
              {{ 'TESTS.QUESTION' | translate }}
              {{ session.currentQuestionIndex + 1 }} / {{ session.questions.length }}
            </small>
          </div>
          <button
            class="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
            (click)="exitTest()">
            <i class="fas fa-sign-out-alt"></i>
            <span class="d-none d-sm-inline">{{ 'BUTTONS.EXIT' | translate }}</span>
          </button>
        </div>
      </app-quiz-header>

      <!-- Enregistrement en echec : l'eleve doit le savoir PENDANT l'epreuve,
           pas en decouvrant a la fin que sa copie n'est jamais partie. -->
      <div *ngIf="enregistrementEnEchec"
           class="save-banner alert alert-warning mb-0 py-2 d-flex align-items-center gap-2"
           role="status" aria-live="polite">
        <i class="fas fa-cloud-arrow-up"></i>
        <strong>{{ 'TESTS.SAVE_FAILED' | translate }}</strong>
        <span class="small">
          — {{ 'TESTS.SAVE_FAILED_DETAIL' | translate:{ count: reponsesNonEnregistrees } }}
        </span>
      </div>

      <!-- Le doute se note en un geste, au lieu d'etre garde en tete pendant
           quarante minutes. Rien ne part au serveur : c'est une aide a la
           navigation, pas une reponse. -->
      <button type="button" class="mark-btn"
              [class.is-marked]="questionCouranteMarquee"
              [attr.aria-pressed]="questionCouranteMarquee"
              (click)="basculerMarque()">
        <i class="fa-solid fa-flag" aria-hidden="true"></i>
        <span>{{ 'TESTS.MARK_FOR_REVIEW' | translate }}</span>
      </button>

      <!-- Question timed-out banner -->
      <div *ngIf="isCurrentQuestionTimedOut"
           class="timeout-banner alert alert-danger mb-0 py-2 d-flex align-items-center gap-2"
           role="alert">
        <i class="fas fa-clock"></i>
        <strong>{{ 'TESTS.QUESTION_TIMED_OUT' | translate }}</strong>
        <span class="small">— {{ 'TESTS.CANNOT_ANSWER_TIMED_OUT' | translate }}</span>
      </div>
    </div>

    <!-- ── Body (scrollable question area) ─────────────────── -->
    <div class="test-body" [class.has-why]="!!currentReason">
      <app-quiz-question
        *ngIf="currentQuestion"
        [questionLabel]="'TESTS.QUESTION'"
        [questionNumber]="session.currentQuestionIndex + 1"
        [totalQuestions]="session.questions.length"
        [title]="currentQuestion.question.title"
        [content]="currentQuestion.question.description"
        [answers]="currentQuestion.question.answerBanks"
        [questionType]="currentQuestion.question.questionType"
        [points]="currentQuestion.question.points"
        [showPoints]="false"
        [selectedAnswers]="session.answers.get(currentQuestion.question.id) || []"
        [disabled]="isCurrentQuestionTimedOut"
        (answerSelected)="selectAnswer(currentQuestion.question.id, $event)">
      </app-quiz-question>

      <!-- Pourquoi cette question : uniquement sur un entraînement composé par
           l'élève. Un élève qui comprend l'intention révise autrement. -->
      <aside class="why" *ngIf="currentReason as reason">
        <div class="why__head">
          <i class="fa-solid fa-bullseye"></i>
          <b>{{ 'PRACTICE.WHY_TITLE' | translate }}</b>
        </div>

        <span class="why__tag" [attr.data-reason]="reason.reason">
          {{ reasonLabelKey(reason.reason) | translate }}
        </span>

        <p class="why__text">
          {{ reasonLabelKey(reason.reason) + '_DESC' | translate: { theme: reason.themeTitle } }}
        </p>

        <ul class="why__facts">
          <li>
            <i class="fa-solid fa-book"></i>
            <span>{{ reason.themeTitle }}</span>
          </li>
          <li *ngIf="reason.neverSeen">
            <i class="fa-solid fa-wand-magic-sparkles"></i>
            <span>{{ 'PRACTICE.WHY_NEVER_SEEN' | translate }}</span>
          </li>
          <li *ngIf="!reason.neverSeen">
            <i class="fa-solid fa-rotate"></i>
            <span>{{ 'PRACTICE.WHY_SEEN_AGAIN' | translate }}</span>
          </li>
        </ul>
      </aside>
    </div>

    <!-- ── Footer (navigator + navigation buttons) ─────────── -->
    <div class="test-footer" [attr.dir]="isLTR ? 'ltr' : 'rtl'">

      <!-- Sur téléphone, le repère se replie derrière cette ligne : la grille
           sert à revenir sur une question laissée de côté, l'énoncé se lit en
           permanence. Masquée au-dessus de 576 px, où la place ne manque pas. -->
      <button
        type="button"
        class="repere-bascule"
        [attr.aria-expanded]="repereOuvert"
        aria-controls="repere-questions"
        (click)="basculerRepere()">
        <i class="fas fa-table-cells" aria-hidden="true"></i>
        <span class="repere-bascule__titre">{{ 'TESTS.QUESTION_NAVIGATOR' | translate }}</span>
        <span class="repere-bascule__position">
          {{ session.currentQuestionIndex + 1 }} / {{ totalQuestions }}
        </span>
        <i class="fas" [ngClass]="repereOuvert ? 'fa-chevron-down' : 'fa-chevron-up'"
           aria-hidden="true"></i>
      </button>

      <!-- Une page de bulles, celle de la question courante. Quatre-vingt-deux
           bulles d'un bloc — la taille réelle des diagnostics — faisaient
           quatorze rangées sur un téléphone. -->
      <div class="repere" id="repere-questions" [class.est-ouvert]="repereOuvert">
        <div class="question-navigator" #navigateur>
          <div class="question-navigator__grid">
            <button
              *ngFor="let i of indicesAffiches; trackBy: parIndice"
              class="btn question-nav-btn"
              [ngClass]="{
                'btn-primary':          i === session.currentQuestionIndex,
                'btn-success':          estRepondue(i) && i !== session.currentQuestionIndex,
                'btn-danger':           session.timedOutQuestions.has(i) && i !== session.currentQuestionIndex,
                'btn-outline-secondary':!estRepondue(i) && !session.timedOutQuestions.has(i) && i !== session.currentQuestionIndex
              }"
              [disabled]="!canNavigateToQuestion(i)"
              (click)="canNavigateToQuestion(i) && jumpToQuestion(i)"
              [title]="getQuestionButtonTitle(i)">
              {{ i + 1 }}
              <i *ngIf="i === session.currentQuestionIndex"
                 class="fas fa-caret-up nav-icon text-white"></i>
              <i *ngIf="!canNavigateToQuestion(i) && !session.timedOutQuestions.has(i)"
                 class="fas fa-lock nav-icon text-muted"></i>
              <i *ngIf="session.timedOutQuestions.has(i)"
                 class="fas fa-clock nav-icon text-white"></i>
            </button>
          </div>
        </div>

        <!-- Feuilleter le repère, sans changer de question. Les flèches suivent
             le sens de lecture de l'épreuve, comme celles du bas. -->
        <div class="navigator-pager" *ngIf="navigateurPagine">
          <button
            type="button"
            class="navigator-pager__bt"
            [disabled]="pageNavigateur === 0"
            [attr.aria-label]="'TESTS.NAV_PAGE_PREVIOUS' | translate"
            (click)="pagePrecedente()">
            <i [class]="isLTR ? 'fa fa-chevron-left' : 'fa fa-chevron-right'" aria-hidden="true"></i>
          </button>

          <span class="navigator-pager__plage" aria-live="polite">
            {{ premiereBulleAffichee }}–{{ derniereBulleAffichee }}
            <span class="navigator-pager__total">/ {{ totalQuestions }}</span>
          </span>

          <button
            type="button"
            class="navigator-pager__bt"
            [disabled]="pageNavigateur >= nombreDePages - 1"
            [attr.aria-label]="'TESTS.NAV_PAGE_NEXT' | translate"
            (click)="pageSuivante()">
            <i [class]="isLTR ? 'fa fa-chevron-right' : 'fa fa-chevron-left'" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- Hors du navigateur : le compte des réponses ne doit pas défiler avec
           les bulles, il répond à « ai-je fini ? ». -->
      <div class="progress-summary text-muted">
        <span class="d-flex align-items-center gap-1">
          <i class="fas fa-check-circle text-success"></i>
          <strong class="text-dark">{{ answeredCount }}</strong>
          {{ 'TESTS.ANSWERED' | translate }}
        </span>
        <span class="d-flex align-items-center gap-1">
          <i class="fas fa-circle text-secondary"></i>
          <strong class="text-dark">{{ totalQuestions - answeredCount }}</strong>
          {{ 'TESTS.REMAINING' | translate }}
        </span>
      </div>

      <!-- Prev / Next / Submit -->
      <div class="nav-buttons-row">
        <app-button
          *ngIf="!isFirstQuestion"
          (click)="goToPreviousQuestion()"
          [label]="'BUTTONS.PREVIOUS' | translate"
          [customClasses]="'btn-previous p-2 d-flex align-items-center gap-2'"
          [type]="'button'"
          [icon]="isLTR ? 'fa fa-chevron-left' : 'fa fa-chevron-right'"
          [iconPosition]="'start'">
        </app-button>

        <div class="flex-grow-1"></div>

        <app-button
          *ngIf="!isLastQuestion"
          (click)="goToNextQuestion()"
          [label]="'BUTTONS.NEXT' | translate"
          [customClasses]="'btn-next p-2 d-flex align-items-center gap-2'"
          [type]="'button'"
          [disabled]="isNextButtonDisabled"
          [icon]="isLTR ? 'fa fa-chevron-right' : 'fa fa-chevron-left'"
          [iconPosition]="'end'">
        </app-button>

        <!-- La remise apparaît dès que tout est traité, où que l'élève se
             trouve : elle était réservée à la dernière question, et sur un
             diagnostic de 52 questions, revenir vérifier la 30ᵉ faisait
             disparaître le seul moyen de rendre la copie. -->
        <app-button
          *ngIf="peutRemettre"
          (click)="submitTest()"
          [label]="'TESTS.SUBMIT_TEST' | translate"
          [customClasses]="'btn-next p-2 d-flex align-items-center gap-2'"
          [type]="'button'"
          [icon]="'fa fa-check-circle'"
          [iconPosition]="'end'">
        </app-button>
      </div>

    </div>
  </div>

</ng-template>
